/**
 * V3.1o — Section "Utilisateurs autorisés" de l'admin WhatsApp.
 *
 * Permet à un admin CRM de :
 *   - lister les identités WhatsApp enrôlées (utilisateur CRM, numéro,
 *     profil appliqué, statut activé/désactivé)
 *   - enrôler un utilisateur CRM existant avec son numéro WhatsApp et
 *     un profil de permissions (admin / employee / sous_traitant)
 *   - activer/désactiver une identité (toggle)
 *   - appliquer explicitement un profil à une identité existante
 *
 * Utilise strictement les endpoints backend existants :
 *   - GET    /api/whatsapp/admin/identities?include_disabled=true
 *   - POST   /api/whatsapp/admin/identities
 *   - PATCH  /api/whatsapp/admin/identities/{id}
 *   - POST   /api/whatsapp/admin/identities/{id}/apply-profile
 *   - GET    /api/whatsapp/admin/permission-profiles
 *   - GET    /api/admin/users  (users CRM existants pour le dropdown)
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Users, UserPlus, Power, PowerOff, RefreshCw, Loader2, Shield,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';


const PROFILE_LABELS = {
  admin: 'Administrateur',
  employee: 'Collaborateur',
  sous_traitant: 'Sous-traitant',
};

const PROFILE_BADGES = {
  admin: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  employee: 'bg-sky-100 text-sky-700 border-sky-200',
  sous_traitant: 'bg-amber-100 text-amber-800 border-amber-200',
};


export default function WhatsAppIdentitiesPanel() {
  const [identities, setIdentities] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrollOpen, setEnrollOpen] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [idR, prR, usR] = await Promise.all([
        api.get('/whatsapp/admin/identities', {
          params: { include_disabled: true },
        }),
        api.get('/whatsapp/admin/permission-profiles'),
        api.get('/admin/users'),
      ]);
      setIdentities(idR.data?.items || []);
      setProfiles(prR.data?.items || []);
      const uList = usR.data?.items || usR.data?.users || usR.data || [];
      setUsers(Array.isArray(uList) ? uList : []);
    } catch (e) {
      toast.error('Erreur chargement identités WhatsApp');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const userById = useMemo(() => {
    const m = {};
    users.forEach(u => { if (u?.user_id) m[u.user_id] = u; });
    return m;
  }, [users]);

  const guessProfile = (identity) => {
    // Détecte le profil appliqué en comparant les scopes
    if (!identity.permissions || identity.permissions.length === 0) {
      return null;  // héritage dynamique
    }
    for (const prof of profiles) {
      const sc = prof.scopes || [];
      if (sc.length === identity.permissions.length
          && sc.every(s => identity.permissions.includes(s))) {
        return prof.profile_id;
      }
    }
    return 'custom';
  };

  const handleToggle = async (identity) => {
    const next = !identity.enabled;
    try {
      await api.patch(`/whatsapp/admin/identities/${identity.identity_id}`,
                       { enabled: next });
      toast.success(next ? 'Identité activée.' : 'Identité désactivée.');
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur mise à jour');
    }
  };

  const handleApplyProfile = async (identity, profile_id) => {
    try {
      await api.post(
        `/whatsapp/admin/identities/${identity.identity_id}/apply-profile`,
        { profile_id },
      );
      toast.success(`Profil « ${PROFILE_LABELS[profile_id] || profile_id} » appliqué.`);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur application profil');
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden"
         data-testid="whatsapp-identities-panel">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-slate-600" />
          <h3 className="font-semibold text-slate-900">Utilisateurs autorisés</h3>
          <Badge variant="outline" className="bg-slate-50 border-slate-200 text-slate-700">
            {identities.length}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadAll}
                   data-testid="whatsapp-identities-refresh">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Rafraîchir
          </Button>
          <Button size="sm" onClick={() => setEnrollOpen(true)}
                   data-testid="whatsapp-identities-enroll-btn">
            <UserPlus className="h-3.5 w-3.5 mr-1.5" />
            Enrôler un utilisateur
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
          Chargement…
        </div>
      ) : identities.length === 0 ? (
        <div className="p-8 text-center text-slate-500 text-sm">
          Aucun utilisateur enrôlé pour l'instant.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Utilisateur CRM</th>
                <th className="px-4 py-2 text-left font-medium">Numéro</th>
                <th className="px-4 py-2 text-left font-medium">Profil</th>
                <th className="px-4 py-2 text-left font-medium">Statut</th>
                <th className="px-4 py-2 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {identities.map((idn) => {
                const u = idn.user_id ? userById[idn.user_id] : null;
                const current = guessProfile(idn);
                return (
                  <tr key={idn.identity_id}
                       className="border-t border-slate-100 hover:bg-slate-50/50"
                       data-testid={`whatsapp-identity-row-${idn.identity_id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">
                        {idn.display_name || u?.name || '—'}
                      </div>
                      <div className="text-xs text-slate-500">
                        {u?.email || idn.email || idn.user_id || idn.sous_traitant_id || '—'}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-700">
                      {idn.phone_e164}
                    </td>
                    <td className="px-4 py-3">
                      <Select
                        value={current === 'custom' ? '' : (current || '__inherit__')}
                        onValueChange={(v) => handleApplyProfile(idn, v)}
                      >
                        <SelectTrigger className="h-8 w-44 text-xs"
                                        data-testid={`whatsapp-identity-profile-${idn.identity_id}`}>
                          <SelectValue placeholder={current === 'custom'
                            ? 'Personnalisé' : 'Choisir…'} />
                        </SelectTrigger>
                        <SelectContent>
                          {profiles.map(p => (
                            <SelectItem key={p.profile_id} value={p.profile_id}>
                              {PROFILE_LABELS[p.profile_id] || p.label || p.profile_id}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {!current && (
                        <div className="text-[10px] text-slate-400 mt-1">
                          Héritage automatique (CRM role)
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {idn.enabled ? (
                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                          Actif
                        </Badge>
                      ) : (
                        <Badge className="bg-rose-100 text-rose-700 border-rose-200">
                          Désactivé
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant={idn.enabled ? 'outline' : 'default'}
                        size="sm"
                        onClick={() => handleToggle(idn)}
                        data-testid={`whatsapp-identity-toggle-${idn.identity_id}`}
                      >
                        {idn.enabled ? (
                          <><PowerOff className="h-3.5 w-3.5 mr-1" /> Désactiver</>
                        ) : (
                          <><Power className="h-3.5 w-3.5 mr-1" /> Activer</>
                        )}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <EnrollDialog
        open={enrollOpen}
        onClose={() => setEnrollOpen(false)}
        users={users}
        profiles={profiles}
        onDone={async () => { setEnrollOpen(false); await loadAll(); }}
      />
    </div>
  );
}


function EnrollDialog({ open, onClose, users, profiles, onDone }) {
  const [form, setForm] = useState({
    user_id: '', phone_e164: '', profile_id: '', notes: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setForm({ user_id: '', phone_e164: '', profile_id: '', notes: '' });
    }
  }, [open]);

  const canSave = form.user_id && /^\+\d{8,15}$/.test(form.phone_e164)
                   && form.profile_id;

  const submit = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const createRes = await api.post('/whatsapp/admin/identities', {
        phone_e164: form.phone_e164,
        identity_type: 'user',
        user_id: form.user_id,
        notes: form.notes || null,
      });
      const identity_id = createRes.data?.identity_id;
      if (identity_id && form.profile_id) {
        // Applique le profil choisi (sinon ça resterait en héritage dynamique)
        await api.post(
          `/whatsapp/admin/identities/${identity_id}/apply-profile`,
          { profile_id: form.profile_id },
        );
      }
      toast.success('Utilisateur enrôlé.');
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur enrôlement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="whatsapp-enroll-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-slate-600" />
            Enrôler un utilisateur WhatsApp
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs">Utilisateur CRM</Label>
            <Select
              value={form.user_id}
              onValueChange={(v) => setForm(f => ({ ...f, user_id: v }))}
            >
              <SelectTrigger data-testid="whatsapp-enroll-user">
                <SelectValue placeholder="Sélectionner un utilisateur…" />
              </SelectTrigger>
              <SelectContent>
                {users.map(u => (
                  <SelectItem key={u.user_id} value={u.user_id}>
                    {u.name || u.email}
                    <span className="text-xs text-slate-400 ml-2">
                      ({u.email})
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">Numéro WhatsApp (format E.164)</Label>
            <Input
              value={form.phone_e164}
              onChange={(e) => setForm(f => ({ ...f, phone_e164: e.target.value }))}
              placeholder="+33612345678"
              className="font-mono"
              data-testid="whatsapp-enroll-phone"
            />
            <div className="text-[10px] text-slate-400 mt-1">
              Préfixe international, chiffres uniquement — ex. +33612345678
            </div>
          </div>

          <div>
            <Label className="text-xs">Profil WhatsApp</Label>
            <Select
              value={form.profile_id}
              onValueChange={(v) => setForm(f => ({ ...f, profile_id: v }))}
            >
              <SelectTrigger data-testid="whatsapp-enroll-profile">
                <SelectValue placeholder="Sélectionner un profil…" />
              </SelectTrigger>
              <SelectContent>
                {profiles.map(p => (
                  <SelectItem key={p.profile_id} value={p.profile_id}>
                    {PROFILE_LABELS[p.profile_id] || p.label || p.profile_id}
                    <span className="text-xs text-slate-400 ml-2">
                      ({(p.scopes || []).length} scopes)
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">Note (optionnel)</Label>
            <Input
              value={form.notes}
              onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Ex. Chef de chantier équipe parquet"
              data-testid="whatsapp-enroll-notes"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Annuler
          </Button>
          <Button onClick={submit} disabled={!canSave || saving}
                   data-testid="whatsapp-enroll-submit">
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
            Enrôler
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
